import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AppConfig } from 'app/app.config';
import { LocalStorageService } from 'app/services/local-storage.service';
import * as _ from 'lodash';
import * as lunr from 'lunr';

@Component({
    selector: 'psm-cities-selector',
    templateUrl: 'citiesSelector.component.html',
    styleUrls: ['citiesSelector.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => CitiesSelectorComponent)
        }
    ]
})

export class CitiesSelectorComponent implements ControlValueAccessor, OnInit {
    private readonly maxNumberOfCitiesInList = 5;

    private _disabled: boolean = false;
    public get disabled() { return this._disabled; }

    private registeredOnTouched: any;
    private registeredOnChange: any;

    private matches: string[] = [];
    public filteredMatches: string[] = [];
    public prefix: string;

    private _value: Set<string> = new Set();
    @Output() public valueChange = new EventEmitter<any>();

    @Input()
    public get value(): Set<string> {
        return this._value;
    }

    public set value(value: Set<string>) {
        if (this.areEqual(value, this._value)) { return; }
        this._value = value;
        this.emitValueChanged();
    }

    public isLoading: boolean = false;
    public isFocused: boolean = false;
    public isHoveringList: boolean = false;
    private lunrIndex: lunr.Index;

    constructor(private http: HttpClient, private appConfig: AppConfig, private storage: LocalStorageService) { }

    public async ngOnInit() {
        this.isLoading = this._disabled = true;
        const index = await this.storage.get<object>('widgets/weather/cities.index');
        if (index) {
            this.lunrIndex = lunr.Index.load(index);
            this.isLoading = this._disabled = false;
        } else {
            // Update the index from the data on server
            this.http.get<string[]>('assets/cities.json').toPromise().then(data => {

                this.lunrIndex = lunr(builder => {
                    builder.field('city');
                    builder.field('country');
                    builder.field('id');
                    builder.ref('id');
                    for (const cityStr of data) {
                        const idx = cityStr.lastIndexOf(',');
                        builder.add({
                            city: cityStr.substring(0, idx).trim(),
                            country: cityStr.substring(idx + 1).trim(),
                            id: cityStr
                        });
                    }
                    this.isLoading = this._disabled = false;
                });
                this.storage.set<object>('widgets/weather/cities.index', this.lunrIndex.toJSON());
                this.isLoading = this._disabled = false;
            });
        }
    }

    public search() {
        this.filteredMatches = [];
        if (this.prefix.length < 2) return;

        this.isLoading = true;
        const results = this.lunrIndex.search(this.prefix + '*').slice(0, this.maxNumberOfCitiesInList);
        for (const result of results) {
            this.filteredMatches.push(result.ref);
        }

        this.isLoading = false;
    }
    public removeCity(city: string) {
        if (this.disabled || !city) { return; }

        this._value.delete(city);
        this.emitValueChanged();
    }

    public citySelected(city) {
        if (this.disabled || !city) { return; }

        this._value.add(city);
        this.emitValueChanged();
    }

    private emitValueChanged() {
        this.valueChange.emit(this.value);
        if (this.registeredOnChange) {
            this.registeredOnChange(this.value);
        }
    }

    private areEqual(set1: Set<string>, set2: Set<string>) {
        if (set1.size !== set2.size) { return false; }

        for (const value of Array.from(set1)) {
            if (!set2.has(value)) { return false; }
        }

        return true;
    }

    // ControlValueAccessor implementation
    public writeValue(obj: any): void {
        this.value = obj;
    }

    public registerOnChange(fn: any): void {
        this.registeredOnChange = fn;
    }

    public registerOnTouched(fn: any): void {
        this.registeredOnTouched = fn;
    }

    public setDisabledState?(isDisabled: boolean): void {
        this._disabled = isDisabled;
    }
}



// WEBPACK FOOTER //
// ./src/app/views/presentations/widgets/weather/citiesSelector.component.ts